import React, { Fragment, useRef, useEffect } from 'react'
import styled from '@emotion/styled'
import { graphql } from 'gatsby'
import Image from 'gatsby-image'
import { Parallax } from 'react-scroll-parallax'
import { chunk } from 'lodash-es'

import { Power2, Power1 } from '../../lib/gsap'
import Swiper from '../../lib/swiper4'

import { useNavbarTheme } from '../../contexts/NavbarTheme'

import { useControlledTimeline } from '../../hooks/animation'
import useInView from '../../hooks/useInView'

import theme from '../../styles/theme'
import { media, rem, lines, min } from '../../styles/tools'

import SEO from '../../components/SEO'
import Wrapper, { wrapperPadding } from '../../components/Wrapper'
import Shell from '../../components/Shell'
import Space from '../../components/Space'
import Link from '../../components/Link'
import BackgroundText from '../../components/BackgroundText'
import ChallengeUsFooter from '../../components/ChallengeUsFooter'
import { ContentAboveFooter } from '../../components/Footer'
import { Fade, Slide, AutoSlide, AutoScale } from '../../components/Animated'
import ShowCells from '../../components/ShowCells'
import LogoCell from '../../components/LogoCell'
import AwardCell from '../../components/AwardCell'
import HelloFab from '../../components/HelloFab'
import StaggerTriad from '../../components/StaggerTriad'
import {
  Headline,
  BoldText,
  WideText,
  Strikethrough,
} from '../../styles/typography'
import TextUnderline from '../../components/TextUnderline'

import { projectURL } from '../../projects'

import TheDesigner from '../../assets/pages/agency/TheDesigner'
import TheProfessor from '../../assets/pages/agency/TheProfessor'

import webbyAwards from '../../assets/awards/webby-awards.svg'
import addyAwards from '../../assets/awards/addy-awards.svg'
import how from '../../assets/awards/how.svg'
import W3Awards from '../../assets/awards/W3-awards.svg'
import ava from '../../assets/awards/ava.svg'
import commarts from '../../assets/awards/commarts.svg'
import communicator from '../../assets/awards/communicator.svg'
import davey from '../../assets/awards/davey.svg'

import acHotelsLogo from '../../assets/logos/ac-hotels-logo.svg'
import amc from '../../assets/logos/amc.svg'
import dignityHealth from '../../assets/logos/dignity-health.svg'
import espnLogo from '../../assets/logos/espn-logo.svg'
import foxLogo from '../../assets/logos/fox-logo.svg'
import motiv from '../../assets/logos/motiv.svg'
import paramount from '../../assets/logos/paramount.svg'
import tennisChannel from '../../assets/logos/tennis-channel.svg'
import kraftFoodsLogo from '../../assets/logos/kraft-foods-logo.svg'
import sothebys from '../../assets/logos/sothebys.svg'
import wHotel from '../../assets/logos/w-hotel.svg'
import westfield from '../../assets/logos/westfield.svg'

import team from '../../team'

const HeaderAnimation = ({ image, ...props }) => {
  const domEl = useRef()
  const [setInViewRef, inView] = useInView({ rootMargin: '-50% 0px' })
  const tl = useControlledTimeline({}, inView)

  useEffect(() => {
    const theDesigner = domEl.current.querySelector('.the-designer')
    const pathA01 = domEl.current.querySelector('#header-arrow-mask-a-path-01')
    const pathA02 = domEl.current.querySelector('#header-arrow-mask-a-path-02')
    const pathA03 = domEl.current.querySelector('#header-arrow-mask-a-path-03')
    const theProfessor = domEl.current.querySelector('.the-professor')
    const pathB01 = domEl.current.querySelector('#header-arrow-mask-b-path-01')
    const pathB02 = domEl.current.querySelector('#header-arrow-mask-b-path-02')
    const pathB03 = domEl.current.querySelector('#header-arrow-mask-b-path-03')
    const ease = Power2.easeInOut

    tl.from(theDesigner, 0.6, {
      x: 50,
      opacity: '0',
      ease: Power2.easeOut,
    })
    tl.from(pathA01, 1, { drawSVG: '0%', ease: ease }, '-=.6')
    tl.from(pathA02, 0.6, { drawSVG: '0%', ease: ease }, '-=.3')
    tl.from(pathA03, 0.6, { drawSVG: '0%', ease: ease }, '-=.3')

    tl.from(
      theProfessor,
      0.6,
      { x: -50, opacity: '0', ease: Power2.easeOut },
      '-=1'
    )
    tl.from(pathB01, 1, { drawSVG: '0%', ease: ease }, '-=.6')
    tl.from(pathB02, 0.6, { drawSVG: '0%', ease: ease }, '-=.3')
    tl.from(pathB03, 0.6, { drawSVG: '0%', ease: ease }, '-=.3')
  }, [])

  return (
    <div
      ref={ref => {
        setInViewRef(ref)
        domEl.current = ref
      }}
      css={{
        position: 'relative',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
      {...props}
    >
      <div
        css={{
          [media.max.tablet]: { display: 'none' },
          width: '10%',
          position: 'absolute',
          zIndex: 1,
          top: '21%',
          left: '35%',
          pointerEvents: 'all',
        }}
      >
        <Link to="/agency/anthony-wiktor">
          <TheDesigner />
          <svg
            viewBox="0 0 96.5 55.6"
            fill="#fff"
            style={{
              width: '45%',
              margin: '-2% 0 0 -15%',
            }}
          >
            <mask id="header-arrow-mask-a-01">
              <path
                id="header-arrow-mask-a-path-01"
                d="M96.5-.3C85.1 21 69 29.2 41.8 39.7c-28.3 11-40.5 2.4-40.5 2.4"
                fill="none"
                stroke="#fff"
                strokeWidth="4"
              />
            </mask>
            <mask id="header-arrow-mask-a-02">
              <path
                id="header-arrow-mask-a-path-02"
                d="M-.1 46.9s2-2.8 17.7-8.5c12.8-4.7 14.2-8.4 14.9-9.6"
                fill="none"
                stroke="#fff"
                strokeWidth="3.2"
              />
              <path
                id="header-arrow-mask-a-path-03"
                d="M-.1 46.3c3.2-1 13.9 1.1 18 3 3.3 1.6 15.4 5 18 6"
                fill="none"
                stroke="#fff"
                strokeWidth="3"
              />
            </mask>
            <path
              mask="url(#header-arrow-mask-a-01)"
              d="M95.7.2c-2.8 3.9-5.1 8.3-8.3 11.8-3 3.2-6.6 5.9-10.1 8.4-7.1 5-14.8 9.2-22.8 12.7-7.8 3.4-16.1 6.3-24.3 8.5a43 43 0 0 1-27-.1c-1.9-.7-2.7 2.3-.8 3.1 16.9 6.3 36.4-1.7 52-8.5A115 115 0 0 0 78.6 22c3.7-2.9 7.5-5.9 10.5-9.6 2.9-3.6 5-7.9 7.5-11.8.2-.4-.5-.8-.9-.4z"
            />
            <path
              mask="url(#header-arrow-mask-a-02)"
              d="M35.6 54.6c-5.9-1.7-11.8-4.8-17.7-6.9-2.8-1-5.8-2.7-8.8-3.3-2.4-.5-5.3-.1-7.7-.1l.7 2.8c6.2-3.9 13.5-6.3 20.2-9.3 3.6-1.6 9-3.4 10.8-7.3.6-1.3-.9-2.5-2-1.5-8.6 7.8-20.8 9.6-30.5 15.6-1.2.8-.8 2.8.7 2.8 2.8 0 5.8-.3 8.6.3 3 .7 6 1.9 9 2.8 5.4 1.6 10.8 4.1 16.3 5.3.8-.1 1-1.1.4-1.2z"
            />
          </svg>
        </Link>
      </div>
      <div
        css={{
          [media.max.tablet]: { display: 'none' },
          width: '10%',
          position: 'absolute',
          zIndex: 1,
          top: '21%',
          left: '61%',
          pointerEvents: 'all',
        }}
      >
        <Link to="/agency/freddy-nager">
          <TheProfessor />
          <svg
            viewBox="0 0 93.1 68.3"
            fill="#fff"
            style={{
              width: '38%',
              margin: '5% 0 0 66%',
            }}
          >
            <mask id="header-arrow-mask-b-01">
              <path
                id="header-arrow-mask-b-path-01"
                d="M0 .1C7.5 13 8 56.2 93.2 55"
                fill="none"
                stroke="#fff"
                strokeWidth="6"
              />
            </mask>
            <mask id="header-arrow-mask-b-02">
              <path
                id="header-arrow-mask-b-path-02"
                d="M88.5 53.9c-6.1-2.2-16.6-3.8-30.4-14.2"
                fill="none"
                stroke="#fff"
                strokeWidth="5"
              />
              <path
                id="header-arrow-mask-b-path-03"
                d="M88.2 56.2c-.6.7-16.6 1.6-31.1 11.5"
                fill="none"
                stroke="#fff"
                strokeWidth="5"
              />
            </mask>
            <path
              mask="url(#header-arrow-mask-b-01)"
              d="M0 .8c1 18.7 15.3 36.1 31.2 45 8.5 4.8 18 7.6 27.6 9.3 4.8.9 9.8 1.4 14.7 1.8 4.6.4 10 1.4 14.4.2 2.2-.6 2.1-3.7 0-4.3-3.7-1.2-8.2-.7-12.1-.9-4.3-.3-8.6-.6-12.9-1.2a91 91 0 0 1-26.6-7.5C20.1 35.3 3.9 19.3 1.7.8 1.6-.2-.1-.3 0 .8z"
            />
            <path
              mask="url(#header-arrow-mask-b-02)"
              d="M57.9 41.5c10.5 7 20.8 13.8 33.5 15.5v-3.4a63 63 0 0 0-19.5 5c-3.2 1.4-6.2 3.2-9.5 4.3-1.8.7-3.8 1-5.2 2.4-1.5 1.6.8 3.9 2.4 2.4 1.2-1.2 4.3-1.5 5.8-2.2 3.2-1.4 6.2-2.9 9.5-4.1A83 83 0 0 1 92 56.8c2-.3 1.3-3.2-.4-3.3-12.2-.9-22.5-7.3-32.8-13.3-1-.4-1.6.8-.9 1.3z"
            />
          </svg>
        </Link>
      </div>
      <Image
        fluid={image}
        css={{
          [media.laptop]: {
            marginRight: -theme.barWidth.laptop,
            marginLeft: -theme.barWidth.laptop,
          },
          [media.max.tabletMd]: { minHeight: '87vw' },
        }}
      />
    </div>
  )
}

const AvLogoAnimation = ({ active, ...props }) => {
  const tl = useControlledTimeline({}, active)
  const domEl = useRef()

  useEffect(() => {
    const groups = domEl.current.querySelectorAll('g')
    tl.staggerFrom(
      groups,
      1.2,
      { x: 60, opacity: 0, ease: Power1.easeOut },
      0.6
    )
  }, [tl])

  return (
    <svg viewBox="0 0 290 8" ref={domEl} fill="currentColor" {...props}>
      <g>
        <path d="M5.3,6.3H2.4L1.7,7.8H0L3.9,0l3.9,7.8H6.1L5.3,6.3z M4.7,4.9L3.9,3.3L3.1,4.9H4.7z" />
        <path
          d="M13.6,0.2h2.7C18.5,0.2,20,1.9,20,4s-1.5,3.8-3.7,3.8h-2.7C13.6,7.8,13.6,0.2,13.6,0.2z M18.5,4.1
        c0-1.4-1-2.5-2.4-2.5h-1v4.9h1C17.5,6.5,18.5,5.5,18.5,4.1z"
        />
      </g>
      <g>
        <circle cx="27.4" cy="4.3" r="0.8" />
        <path d="M40.5,8l-3.9-7.8h1.7l2.2,4.5l2.2-4.5h1.7L40.5,8z" />
        <path d="M51.6,0.2h1.5v7.6h-1.5V0.2z" />
        <path
          d="M66.3,6.3c-0.7,1-1.9,1.6-3.1,1.6c-2.1,0-3.9-1.7-3.9-3.9c0-2.1,1.7-3.9,3.9-3.9c1.1,0,2.1,0.5,2.9,1.2l-1,0.9
          c-0.5-0.4-1.2-0.8-1.9-0.8c-1.4,0-2.4,1.1-2.4,2.5s1,2.5,2.4,2.5c0.8,0,1.7-0.5,2.2-1.1L66.3,6.3z"
        />
      </g>
      <g>
        <circle cx="74.4" cy="4.3" r="0.8" />
        <path d="M81.9,0.2h5.5v1.4h-2v6.2h-1.5V1.6h-2V0.2z" />
        <path
          d="M100.6,4c0,2.1-1.7,3.9-3.9,3.9c-2.1,0-3.9-1.7-3.9-3.9c0-2.1,1.7-3.9,3.9-3.9C98.8,0.1,100.6,1.8,100.6,4z
          M99,4c0-1.4-1-2.5-2.4-2.5S94.3,2.6,94.3,4s1,2.5,2.4,2.5S99,5.4,99,4z"
        />
        <path
          d="M109.6,5h-1.3v2.8h-1.5V0.2h3.6c1.3,0,2.3,1,2.3,2.4c0,1-0.6,1.9-1.4,2.2l1.5,3H111L109.6,5z M111.1,2.6
          c0-0.6-0.4-1-0.9-1h-2v2h2C110.7,3.6,111.1,3.2,111.1,2.6z"
        />
      </g>
      <g>
        <circle cx="120.4" cy="4.3" r="0.8" />
        <path d="M130.4,6.4h3.8v1.4h-5.3V0.2h5.3v1.4h-3.8v1.9h3v1.3h-3V6.4z" />
        <path d="M147.8,0v7.8h-1.5v-4l-2.1,2.1L142,3.8v4h-1.5V0l3.7,3.8L147.8,0z" />
      </g>
    </svg>
  )
}

const TeamSlide = ({
  as: As = 'div',
  data: { slug, fullName, firstName, position, disabled },
  image,
  ...props
}) => {
  const TeamLink = disabled ? 'div' : Link

  return (
    <As
      css={{
        ...(disabled && { pointerEvents: 'none' }),
        listStyle: 'none',
        '.team-slide__link': {
          transition: '.3s',
          opacity: 0,
        },
        '.team-slide__img': {
          overflow: 'hidden',
          '> img': {
            transform: 'scale(1.001)',
            transition: '.3s',
          },
        },
        [media.hover]: {
          ':hover': {
            '.team-slide__box': {
              transform: 'translateY(-50px)',
            },
            '.team-slide__link': {
              opacity: 1,
            },
            '.team-slide__img > img': {
              transform: 'scale(1.06)',
            },
          },
        },
      }}
      {...props}
    >
      <TeamLink to={`/agency/${slug}`} className="hoverWideText">
        <Image
          css={{
            marginLeft: 8,
            marginRight: 8,
            backgroundColor: '#212121',
          }}
          className="team-slide__img"
          fluid={image}
        />
        <div
          className="team-slide__box"
          css={{
            position: 'relative',
            marginTop: -80,
            [min(1200)]: { marginTop: -110 },
            marginRight: 28,
            marginLeft: -20,
            transition: '.3s',
            cursor: disabled ? 'move' : 'pointer',
          }}
        >
          <BoldText
            as="h3"
            css={{
              fontSize: 22,
              [min(1200)]: { fontSize: 34 },
              lineHeight: 1.22,
            }}
          >
            {fullName}
            <br />
            {position}
          </BoldText>
          <Space y={20} />
          <WideText className="team-slide__link">Meet {firstName}</WideText>
        </div>
      </TeamLink>
    </As>
  )
}

const TeamSliderWrapper = styled.div(
  {
    width: '80vw',
    '.swiper-container': {
      width: '100%',
      position: 'relative',
      zIndex: 1,
      userSelect: 'none',
      cursor: 'move',
    },
    '.swiper-wrapper': {
      width: '100%',
      display: 'flex',
      transitionProperty: 'transform',
      boxSizing: 'content-box',
      willChange: 'transform',
    },
    '.swiper-slide': {
      flex: '1 0 100%',
      transition: 'opacity .5s',
      willChange: 'opacity',
    },
    '& *': {
      cursor: 'inherit',
    },
    [media.max.tabletMd]: {
      '.swiper-slide-next': {
        opacity: 0.3,
      },
    },
    [media.tabletMd]: {
      width: 'calc(66.666vw - 104px)',

      '.swiper-slide': {
        flex: '1 0 50%',
      },

      '.swiper-slide-next + *, .swiper-slide-next + * ~ .swiper-slide': {
        opacity: 0.4,
      },
    },
  },
  ({ wide }) =>
    wide && {
      '.swiper-slide-next': {
        opacity: 0.3,
      },
      '.swiper-slide': {
        flex: '1 0 100% !important',
      },
    }
)

const TeamSlider = ({ images, ...props }) => {
  const root = useRef()

  useEffect(() => {
    const slider = new Swiper(root.current, {
      slidesPerView: 2,
      breakpoints: { 768: { slidesPerView: 1 } },
    })
    return () => slider.destroy()
  }, [])

  return (
    <TeamSliderWrapper {...props}>
      <div className="swiper-container" ref={root}>
        <ul className="swiper-wrapper">
          {team.map(data => (
            <TeamSlide
              key={data.slug}
              as="li"
              className="swiper-slide"
              data={data}
              image={images[data.firstName.toLowerCase()]}
            />
          ))}
        </ul>
      </div>
    </TeamSliderWrapper>
  )
}

export const query = graphql`
  query {
    header: file(relativePath: { eq: "pages/agency/header.png" }) {
      ...windowWide
    }
    magazine: file(relativePath: { eq: "pages/agency/magazine.png" }) {
      ...textContainerWide
    }
    img1: file(relativePath: { eq: "pages/agency/img1.png" }) {
      ...textContainerWide
    }
    img2: file(relativePath: { eq: "pages/agency/img2.png" }) {
      ...textContainerWide
    }
    anthony: file(relativePath: { eq: "team/team_anthony.jpg" }) {
      ...smallImage
    }
    ara: file(relativePath: { eq: "team/team_ara.jpg" }) {
      ...smallImage
    }
    diran: file(relativePath: { eq: "team/team_diran.jpg" }) {
      ...smallImage
    }
    freddy: file(relativePath: { eq: "team/team_freddy.jpg" }) {
      ...smallImage
    }
    garen: file(relativePath: { eq: "team/team_garen.jpg" }) {
      ...smallImage
    }
    greg: file(relativePath: { eq: "team/team_greg.jpg" }) {
      ...smallImage
    }
    kajsa: file(relativePath: { eq: "team/team_kajsa.jpg" }) {
      ...smallImage
    }
    kasia: file(relativePath: { eq: "team/team_kasia.jpg" }) {
      ...smallImage
    }
    maciej: file(relativePath: { eq: "team/team_maciej.jpg" }) {
      ...smallImage
    }
    ralph: file(relativePath: { eq: "team/team_ralph.jpg" }) {
      ...smallImage
    }
    sarah: file(relativePath: { eq: "team/team_sarah.jpg" }) {
      ...smallImage
    }
    taylor: file(relativePath: { eq: "team/team_taylor.jpg" }) {
      ...smallImage
    }
    tina: file(relativePath: { eq: "team/team_tina.jpg" }) {
      ...smallImage
    }
    vince: file(relativePath: { eq: "team/team_vince.jpg" }) {
      ...smallImage
    }
  }
`

export default ({ data }) => {
  const header = data.header.childImageSharp.fluid
  const magazine = data.magazine.childImageSharp.fluid
  const img1 = data.img1.childImageSharp.fluid
  const img2 = data.img2.childImageSharp.fluid

  const teamImages = {
    anthony: data.anthony.childImageSharp.fluid,
    ara: data.ara.childImageSharp.fluid,
    diran: data.diran.childImageSharp.fluid,
    freddy: data.freddy.childImageSharp.fluid,
    garen: data.garen.childImageSharp.fluid,
    greg: data.greg.childImageSharp.fluid,
    kajsa: data.kajsa.childImageSharp.fluid,
    kasia: data.kasia.childImageSharp.fluid,
    maciej: data.maciej.childImageSharp.fluid,
    ralph: data.ralph.childImageSharp.fluid,
    sarah: data.sarah.childImageSharp.fluid,
    taylor: data.taylor.childImageSharp.fluid,
    tina: data.tina.childImageSharp.fluid,
    vince: data.vince.childImageSharp.fluid,
  }

  useNavbarTheme('dark')
  const [textOnWhiteRef, textOnWhiteInView] = useInView()
  const [definitionRef, definitionInView] = useInView()

  return (
    <Fragment>
      <SEO title="Our Agency" />

      <Shell css={{ overflow: 'hidden' }}>
        <ContentAboveFooter>
          <Wrapper>
            <div css={{ backgroundColor: '#202020', ...lines('#2d2d2d') }}>
              <div css={wrapperPadding}>
                <Space y={{ mobile: 120, tabletMd: 200 }} />
                <AutoSlide left css={{ marginLeft: -5 }}>
                  <Headline as="h2">Not like other agencies.</Headline>
                </AutoSlide>
                <Space y={32} />
                <AutoSlide left delay={1 * 100}>
                  <BoldText>
                    Based in Los Angeles, we’re a creative branding agency led
                    by Award-Winning Creative Director{' '}
                    <Link to="/agency/anthony-wiktor">
                      <TextUnderline color="#555" top={0.7} height={[0.3, 0.8]}>
                        Anthony Wiktor
                      </TextUnderline>
                    </Link>{' '}
                    and Marketing Professor{' '}
                    <Link to="/agency/freddy-nager">
                      <TextUnderline color="#555" top={0.7} height={[0.3, 0.8]}>
                        Freddy Tran Nager
                      </TextUnderline>
                    </Link>
                    .
                  </BoldText>
                </AutoSlide>
                <Space y={32} />
                <AutoSlide left delay={2 * 100}>
                  <BoldText>
                    Together, our team of designers and writers, programmers and
                    producers, already has shelves of awards and multi-industry
                    experience from Hollywood studios to Silicon Valley
                    startups.
                  </BoldText>
                </AutoSlide>
                <Space y={32} />
                <AutoSlide left delay={3 * 100}>
                  <BoldText>Yet we wanted to do more...</BoldText>
                </AutoSlide>
              </div>
              <HeaderAnimation image={header} css={{ marginTop: '-3vw' }} />
            </div>
            <Wrapper padded light>
              <AutoSlide left>
                <Headline
                  as="h2"
                  css={{
                    lineHeight: 1,
                    marginTop: '-1.6em',
                    [media.tabletMd]: { marginTop: '-2.35vw' },
                  }}
                >
                  Around here, winning is&nbsp;everything.
                </Headline>
              </AutoSlide>
              <Space y={26} />
              <AutoSlide left>
                <BoldText>
                  It's not just in our name, it's in everything we do.
                </BoldText>
              </AutoSlide>
              <Space y={{ mobile: 50, tabletMd: 110 }} />
              <Fade
                ref={definitionRef}
                inView={definitionInView}
                delay={1 * 150}
              >
                <div
                  css={{
                    position: 'relative',
                    fontFamily: '"Source Serif Pro", serif',
                    [media.tabletMd]: {
                      width: '80%',
                      padding: 50,
                      color: '#999',
                      background: '#222',
                    },
                  }}
                >
                  <div
                    css={{
                      [media.max.tabletMd]: { display: 'none' },
                      width: '43%',
                      position: 'absolute',
                      top: '-18%',
                      right: '-23%',
                    }}
                  >
                    <Parallax y={[-10, 25]}>
                      <Link out href="https://victoryjournal.com/">
                        <Image fluid={magazine} />
                      </Link>
                    </Parallax>
                  </div>
                  <Fade inView={definitionInView} delay={2 * 150}>
                    <div
                      css={{
                        width: '150%',
                        [media.tabletMd]: {
                          width: '100%',
                          color: '#fff',
                        },
                      }}
                    >
                      <AvLogoAnimation active={definitionInView} />
                    </div>
                    <Space y={32} />
                    <BoldText css={{ fontWeight: 400 }}>
                      [meaning: To Victory]
                    </BoldText>
                    <BoldText css={{ color: '#666', fontWeight: 400 }}>
                      [origin: Latin]
                    </BoldText>
                  </Fade>
                  <Space y={22} />
                  <Fade inView={definitionInView} delay={3 * 150}>
                    <BoldText
                      as="ol"
                      css={{
                        width: '90%',
                        paddingLeft: '1.4em',
                        fontWeight: 400,
                        '& li': { marginTop: 12, listStyle: 'decimal' },
                      }}
                    >
                      <li>Movement toward defeating the competition</li>
                      <li>Achievement of success against the odds</li>
                      <li>
                        A battle cry by challenger brands in a heated market
                      </li>
                    </BoldText>
                  </Fade>
                </div>
              </Fade>
              <Space y={70} />
              <div ref={textOnWhiteRef} css={{ maxWidth: '54em' }}>
                <Slide left delay={0 * 100} inView={textOnWhiteInView}>
                  <Headline as="p">It’s in our attitude.</Headline>
                </Slide>
                <Space y={32} />
                <Slide left delay={1 * 100} inView={textOnWhiteInView}>
                  <BoldText>
                    We’re problem{' '}
                    <Strikethrough
                      length={-3.9}
                      inView={textOnWhiteInView}
                      delay={1000}
                    >
                      solvers,
                    </Strikethrough>{' '}
                    seekers, continuously analyzing the market for new
                    developments, opportunities, and challenges. The way we see
                    it, to compete – and to win – we have to be proactive, not
                    just reactive.
                  </BoldText>
                </Slide>
                <Space y={32} />
                <Slide left delay={2 * 100} inView={textOnWhiteInView}>
                  <BoldText>
                    And that’s why we work with{' '}
                    <Strikethrough
                      length={-18.8}
                      inView={textOnWhiteInView}
                      delay={1500}
                    >
                      corporations, businesses, enterprises,
                    </Strikethrough>{' '}
                    challenger brands that{' '}
                    <Strikethrough
                      length={-11}
                      inView={textOnWhiteInView}
                      delay={2000}
                    >
                      follow the status quo
                    </Strikethrough>{' '}
                    dare to win, and that realize long-term success comes from{' '}
                    <Strikethrough
                      length={-14.7}
                      inView={textOnWhiteInView}
                      delay={2500}
                    >
                      spreadsheets and algorithms
                    </Strikethrough>{' '}
                    taking risks that trigger emotions.
                  </BoldText>
                </Slide>
                <Space y={{ mobile: 60, tablet: 100 }} />
                <Space y={{ tabletMd: '8vw' }} />
              </div>
            </Wrapper>
            <div
              css={[
                wrapperPadding,
                { [media.max.tabletMd]: { display: 'none' } },
              ]}
            >
              <div css={{ position: 'relative', marginTop: '-8vw' }}>
                <AutoScale
                  scale={0.9}
                  css={{ width: '75%', position: 'relative', zIndex: 1 }}
                >
                  <Image fluid={img1} />
                </AutoScale>
                <AutoScale
                  scale={0.9}
                  delay={1 * 150}
                  css={{
                    width: '50%',
                    position: 'absolute',
                    zIndex: 0,
                    top: '72%',
                    left: '25%',
                  }}
                >
                  <Image fluid={img2} />
                </AutoScale>
                <AutoScale
                  scale={0.9}
                  delay={2 * 150}
                  css={{
                    width: '50%',
                    position: 'absolute',
                    zIndex: 2,
                    top: '50%',
                    left: '50%',
                    padding: '50px 50px 50px 65px',
                    color: '#000',
                    backgroundColor: '#fff',
                  }}
                >
                  <blockquote
                    css={{
                      position: 'relative',
                      margin: 0,
                      fontFamily: '"Source Serif Pro", serif',
                      fontWeight: 400,
                      fontSize: '1.7vw',
                      lineHeight: 1.2,

                      '&::before, &::after': {
                        fontWeight: 800,
                        fontFamily: 'RidleyGrotesk, sans-serif',
                      },

                      '&::before': {
                        content: '"“"',
                        position: 'absolute',
                        top: '-0.38em',
                        left: '-0.7em',
                        fontSize: '260%',
                      },

                      '&::after': {
                        content: '"”"',
                      },
                    }}
                  >
                    Over many cups of coffee in many cafes, we crafted an agency
                    that would put client brands and goals front and center, no
                    matter whether we’re creating a{' '}
                    <Link to="/work">
                      <TextUnderline color="#ddd" top={0.9} height={[0.3, 0.8]}>
                        logo, a video, an app, or an entire online marketing
                        campaign.
                      </TextUnderline>
                    </Link>
                  </blockquote>
                  <Space y={32} />
                  <WideText as={Link} to="/playbook">
                    LEARN ABOUT OUR PROCESS
                  </WideText>
                </AutoScale>
              </div>
              <Space y="22vw" />
            </div>
            <Space y={{ mobile: 60, tablet: 100 }} />
            <div css={wrapperPadding}>
              <BackgroundText>TEAM</BackgroundText>
            </div>
            <div css={{ [media.tabletMd]: { marginTop: '-6vmax' } }}>
              <div
                css={{
                  [media.max.tabletLg]: { display: 'none' },
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gridGap: 20,
                  padding: '0 64px',
                }}
              >
                {chunk(team, 3).map((triad, i) => (
                  <StaggerTriad key={i}>
                    <TeamSlide
                      key={triad[0].slug}
                      as="li"
                      className="swiper-slide"
                      data={triad[0]}
                      image={teamImages[triad[0].firstName.toLowerCase()]}
                    />
                    <TeamSlide
                      key={triad[1].slug}
                      as="li"
                      className="swiper-slide"
                      data={triad[1]}
                      image={teamImages[triad[1].firstName.toLowerCase()]}
                    />
                    <TeamSlide
                      key={triad[2].slug}
                      as="li"
                      className="swiper-slide"
                      data={triad[2]}
                      image={teamImages[triad[2].firstName.toLowerCase()]}
                    />
                  </StaggerTriad>
                ))}
              </div>
              <div
                css={[
                  wrapperPadding,
                  { [media.tabletLg]: { display: 'none' } },
                ]}
              >
                <AutoSlide left>
                  <TeamSlider images={teamImages} />
                </AutoSlide>
              </div>
            </div>
            <Space y={{ mobile: 100, tablet: 150 }} />
            <Wrapper padded>
              <BackgroundText>WHO WE ARE</BackgroundText>
              <AutoSlide
                left
                css={{ [media.tabletMd]: { marginTop: '-5.5vmax' } }}
              >
                <Headline as="h2">
                  Makers, thinkers, and doers who push boundaries for game
                  changing results.
                </Headline>
              </AutoSlide>
              <Space y={80} />
            </Wrapper>

            <div
              css={{
                display: 'grid',
                gridTemplate: `
                  ". left  ." auto
                  ". .     ." 50px
                  ". right ." auto
                  / 1fr 8fr 1fr
                `,
                [media.tabletMd]: {
                  gridTemplate: `
                  ". left  right ." auto
                  / 1fr 2fr 2fr 1fr
                `,
                },
              }}
            >
              <AutoSlide up css={{ gridArea: 'left' }}>
                <h2
                  css={{
                    display: 'inline-block',
                    font: '34px TungstenMedium, sans-serif',
                    lineHeight: 0.6,
                    borderBottom: 'solid 2px',
                    letterSpacing: '0.2em',
                  }}
                >
                  WE ARE
                </h2>
                <Space y={25} />
                <BoldText as="ul" css={{ lineHeight: 1.4 }}>
                  <li>Business Planners</li>
                  <li>Forward Thinkers</li>
                  <li>Branding Experts</li>
                  <li>Strategists</li>
                  <li>Designers</li>
                  <li>Creative Writers</li>
                  <li>Visual Interpreters</li>
                  <li>Critical Thinkers</li>
                  <li>Collaborators</li>
                  <li>Specialists</li>
                </BoldText>
              </AutoSlide>
              <AutoSlide up delay={150} css={{ gridArea: 'right' }}>
                <h2
                  css={{
                    display: 'inline-block',
                    font: '34px TungstenMedium, sans-serif',
                    lineHeight: 0.6,
                    borderBottom: 'solid 2px',
                    letterSpacing: '0.2em',
                  }}
                >
                  WE AREN'T
                </h2>
                <Space y={25} />
                <BoldText as="ul" css={{ lineHeight: 1.4 }}>
                  <li>A Production Factory</li>
                  <li>A Digital Marketing Firm</li>
                  <li>A PR Firm</li>
                  <li>Freelancers</li>
                  <li>Web Programmers</li>
                  <li>Magicians</li>
                  <li>Click Baiters</li>
                  <li>Social Media Gurus</li>
                  <li>Generalists</li>
                  <li>Self-Indulgent Pretenders</li>
                </BoldText>
              </AutoSlide>
            </div>
            <Space y={{ mobile: 80, tablet: 150 }} />
            <Wrapper padded>
              <BackgroundText>CLIENTS</BackgroundText>
              <ShowCells css={{ [media.tabletMd]: { marginTop: '-6.35vmax' } }}>
                <div
                  css={{
                    display: 'grid',
                    padding: rem(5),
                    gridGap: rem(10),
                    gridTemplateColumns: '1fr 1fr',
                    [media.tablet]: {
                      gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    },
                  }}
                >
                  <Link to={projectURL('ac-hotels')}>
                    <LogoCell>
                      <img
                        width="46%"
                        style={{ transform: 'scale(.8)' }}
                        src={acHotelsLogo}
                        alt="AC hotels logo"
                      />
                    </LogoCell>
                  </Link>
                  <Link to={projectURL('amc-preacher')}>
                    <LogoCell>
                      <img
                        width="60%"
                        style={{ transform: 'scale(.8)' }}
                        src={amc}
                        alt="AMC logo"
                      />
                    </LogoCell>
                  </Link>
                  <Link to={projectURL('dignity-health')}>
                    <LogoCell>
                      <img
                        width="85%"
                        style={{ transform: 'scale(.8)' }}
                        src={dignityHealth}
                        alt="Dignity Health logo"
                      />
                    </LogoCell>
                  </Link>
                  <Link to={projectURL('espn')}>
                    <LogoCell>
                      <img
                        width="60%"
                        style={{ transform: 'scale(.8)' }}
                        src={espnLogo}
                        alt="ESPN logo"
                      />
                    </LogoCell>
                  </Link>
                  <Link to={projectURL('fox-digital')}>
                    <LogoCell>
                      <img
                        width="50%"
                        style={{ transform: 'scale(.8)' }}
                        src={foxLogo}
                        alt="Fox logo"
                      />
                    </LogoCell>
                  </Link>
                  <Link to={projectURL('motiv-ring')}>
                    <LogoCell>
                      <img
                        width="75%"
                        style={{ transform: 'scale(.8)' }}
                        src={motiv}
                        alt="Motiv logo"
                      />
                    </LogoCell>
                  </Link>
                  <Link to={projectURL('paramount-equity-mortgage')}>
                    <LogoCell>
                      <img
                        width="70%"
                        style={{ transform: 'scale(.8)' }}
                        src={paramount}
                        alt="Paramount logo"
                      />
                    </LogoCell>
                  </Link>
                  <Link to={projectURL('tennis-channel')}>
                    <LogoCell>
                      <img
                        width="40%"
                        style={{ transform: 'scale(.8)' }}
                        src={tennisChannel}
                        alt="Tennis Channel logo"
                      />
                    </LogoCell>
                  </Link>
                  <LogoCell solid>
                    <img
                      width="75%"
                      style={{ transform: 'scale(.8)' }}
                      src={kraftFoodsLogo}
                      alt="Kraft Foods logo"
                    />
                  </LogoCell>
                  <LogoCell solid>
                    <img
                      width="75%"
                      style={{ transform: 'scale(.8)' }}
                      src={sothebys}
                      alt="Sothebys logo"
                    />
                  </LogoCell>
                  <LogoCell solid>
                    <img
                      width="72%"
                      style={{ transform: 'scale(.8)' }}
                      src={wHotel}
                      alt="w-hotel logo"
                    />
                  </LogoCell>
                  <LogoCell solid>
                    <img
                      width="75%"
                      style={{ transform: 'scale(.8)' }}
                      src={westfield}
                      alt="Westfield logo"
                    />
                  </LogoCell>
                </div>
              </ShowCells>
              <Space y={{ mobile: 20, laptop: 40, laptopLg: 50 }} />
              <AutoSlide
                up
                translate={50}
                css={{ display: 'flex', justifyContent: 'center' }}
              >
                <WideText as={Link} to="/clients">
                  BRANDS WE WORK WITH
                </WideText>
              </AutoSlide>
            </Wrapper>
            <Space y={{ mobile: 80, tablet: 150 }} />
            <Wrapper padded css={{ [media.tabletMd]: { marginTop: rem(-40) } }}>
              <BackgroundText>AWARDS</BackgroundText>
              <AutoSlide
                left
                css={{
                  [media.max.tabletMd]: { display: 'none' },
                  position: 'relative',
                  zIndex: 2,
                  marginTop: rem(20),
                  [media.tabletMd]: {
                    marginTop: '-6.35vmax',
                    marginBottom: rem(40),
                    marginLeft: rem(-5),
                  },
                }}
              >
                <Headline>In good company...</Headline>
              </AutoSlide>
              <Space mobileSm={40} laptopLg={50} />
              <ShowCells>
                <div
                  css={{
                    display: 'grid',
                    padding: rem(5),
                    gridGap: rem(10),
                    gridTemplateColumns: '1fr 1fr',
                    [media.tablet]: {
                      gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    },
                  }}
                >
                  <LogoCell>
                    <AwardCell count={1}>
                      <img
                        width="60%"
                        src={webbyAwards}
                        alt="Webby Awards logo"
                      />
                    </AwardCell>
                  </LogoCell>
                  <LogoCell>
                    <AwardCell count={1}>
                      <img
                        width="60%"
                        src={addyAwards}
                        alt="Addy Awards logo"
                      />
                    </AwardCell>
                  </LogoCell>
                  <LogoCell>
                    <AwardCell count={1}>
                      <img width="60%" src={how} alt="How Awards logo" />
                    </AwardCell>
                  </LogoCell>
                  <LogoCell>
                    <AwardCell count={10}>
                      <img width="37%" src={W3Awards} alt="W3 Awards logo" />
                    </AwardCell>
                  </LogoCell>
                  <LogoCell>
                    <AwardCell count={1}>
                      <img width="60%" src={ava} alt="AVA logo" />
                    </AwardCell>
                  </LogoCell>
                  <LogoCell>
                    <AwardCell count={1}>
                      <img width="60%" src={commarts} alt="Commarts logo" />
                    </AwardCell>
                  </LogoCell>
                  <LogoCell>
                    <AwardCell count={10}>
                      <img
                        width="60%"
                        src={communicator}
                        alt="communicator logo"
                      />
                    </AwardCell>
                  </LogoCell>
                  <LogoCell>
                    <AwardCell count={5}>
                      <img width="60%" src={davey} alt="davey logo" />
                    </AwardCell>
                  </LogoCell>
                </div>
              </ShowCells>
              <Space y={{ mobile: 20, laptop: 40, laptopLg: 50 }} />
              <AutoSlide
                up
                translate={50}
                css={{ display: 'flex', justifyContent: 'center' }}
              >
                <WideText as={Link} to="/awards">
                  AWARDS AND RECOGNITION
                </WideText>
              </AutoSlide>
            </Wrapper>
            <Space y={{ mobile: 100, tablet: 150 }} />
          </Wrapper>
        </ContentAboveFooter>
        <ChallengeUsFooter />
        <HelloFab />
      </Shell>
    </Fragment>
  )
}
